import { api } from './api.ts'
import { nomeColore, type Colore } from './posta.ts'

/**
 * Risposta di assenza e regole di smistamento di una casella (540): forme dei dati e chiamate all'API.
 * Le rotte rispondono solo a chi ha l'accesso pieno alla casella: 404 a chi non la vede, 409 SOLA_LETTURA
 * a chi la legge soltanto, 409 INTERRUTTORE_SPENTO a webmail spenta.
 */

export type StatoRisposta = 'in_coda' | 'inviata' | 'errore' | 'annullata'

export type RispostaInviata = {
  id: string; destinatario: string; creata_il: string; inviata_il: string | null
  stato: StatoRisposta; errore: string | null
}

export type RispostaAssenza = {
  attiva: boolean
  /** Null: «Risposta automatica: <oggetto del messaggio>». */
  oggetto: string | null
  testo: string
  /** AAAA-MM-GG. */
  dal: string
  al: string | null
  /** Accesa e oggi dentro il periodo. */
  in_corso: boolean
  accesa_il: string | null
  modificata_il: string | null
  giorni_fra_risposte: number
  /** La casella scrive solo dentro CIDIS (533): risponde solo ai colleghi. */
  solo_interni: boolean
  risposte: RispostaInviata[]
}

export type DatiAssenza = {
  attiva: boolean; oggetto: string | null; testo: string; dal: string; al: string | null
  /** «Segna anche nell'agenda» (559): «Non disponibile» negli stessi giorni, senza motivo. */
  segna_in_agenda?: boolean
}

export const leggiAssenza = (casellaId: string) => api.get<RispostaAssenza>(`/posta/caselle/${casellaId}/assenza`)
export const salvaAssenza = (casellaId: string, dati: DatiAssenza) =>
  api.put<Omit<RispostaAssenza, 'risposte' | 'giorni_fra_risposte' | 'solo_interni'> & { indisponibilita_id: string | null }>(
    `/posta/caselle/${casellaId}/assenza`, dati)

export type Campo = 'mittente' | 'destinatario' | 'oggetto'
export type Operatore = 'contiene' | 'non_contiene' | 'e'
export type Condizione = { campo: Campo; operatore: Operatore; valore: string }
/** Bandierina ed etichetta (543): sempre quella per tutti, e un'etichetta per tutti della casella. */
export type Azione =
  | { tipo: 'sposta'; cartella_id: string } | { tipo: 'segna_letta' } | { tipo: 'bandierina'; colore: Colore }
  | { tipo: 'etichetta'; etichetta_id: string } | { tipo: 'ferma' }

export type Regola = {
  id: string
  posizione: number
  attiva: boolean
  combinazione: 'tutte' | 'almeno_una'
  condizioni: Condizione[]
  azioni: Azione[]
  avviso: 'cartella_eliminata' | 'etichetta_eliminata' | null
  modificata_il: string
}

export type DatiRegola = Pick<Regola, 'attiva' | 'combinazione' | 'condizioni' | 'azioni'>
export type CartellaDestinazione = { id: string; nome: string; tipo: string }
/** Un'etichetta per tutti della casella: le sole che una regola mette. */
export type EtichettaRegola = { id: string; nome: string }
export type EsitoApplica = {
  esaminati: number; corrispondenti: number; spostati: number; letti: number; bandierine: number; etichettate: number
  oltre: boolean
}

export const leggiRegole = (casellaId: string) =>
  api.get<{ regole: Regola[]; cartelle: CartellaDestinazione[]; etichette: EtichettaRegola[] }>(
    `/posta/caselle/${casellaId}/regole`)
export const creaRegola = (casellaId: string, dati: DatiRegola) => api.post<Regola>(`/posta/caselle/${casellaId}/regole`, dati)
export const modificaRegola = (id: string, dati: DatiRegola) => api.put<Regola>(`/posta/regole/${id}`, dati)
export const accendiRegola = (id: string, attiva: boolean) => api.post<Regola>(`/posta/regole/${id}/interruttore`, { attiva })
export const spostaRegola = (id: string, verso: 'su' | 'giu') =>
  api.post<{ regole: Regola[] }>(`/posta/regole/${id}/sposta`, { verso })
export const eliminaRegola = (id: string) => api.elimina<{ eliminata: boolean }>(`/posta/regole/${id}`)
export const applicaOra = (casellaId: string) => api.post<EsitoApplica>(`/posta/caselle/${casellaId}/regole/applica`)

export const NOMI_CAMPI: Record<Campo, string> = { mittente: 'Mittente', destinatario: 'Destinatario (A o Cc)', oggetto: 'Oggetto' }
export const NOMI_OPERATORI: Record<Operatore, string> = { contiene: 'contiene', non_contiene: 'non contiene', e: 'è' }
const CAMPI_NELLA_FRASE: Record<Campo, string> = { mittente: 'il mittente', destinatario: 'il destinatario', oggetto: 'l’oggetto' }

/** La regola in parole: «Se il mittente contiene «x» e …: sposta in «Archivio», segna come letta». */
export function descriviRegola(
  r: Pick<Regola, 'combinazione' | 'condizioni' | 'azioni'>, cartelle: CartellaDestinazione[], etichette: EtichettaRegola[],
): { se: string; allora: string } {
  const legame = r.combinazione === 'tutte' ? ' e ' : ' oppure '
  const se = r.condizioni
    .map((c) => `${CAMPI_NELLA_FRASE[c.campo]} ${NOMI_OPERATORI[c.operatore]} «${c.valore}»`)
    .join(legame)
  const allora = r.azioni.map((a) => {
    if (a.tipo === 'sposta') {
      const c = cartelle.find((x) => x.id === a.cartella_id)
      return c ? `sposta in «${c.nome}»` : 'sposta in una cartella che non c’è più'
    }
    if (a.tipo === 'segna_letta') return 'segna come letta'
    if (a.tipo === 'bandierina') return `metti la bandierina ${nomeColore(a.colore)} per tutti`
    if (a.tipo === 'etichetta') {
      const e = etichette.find((x) => x.id === a.etichetta_id)
      return e ? `metti l’etichetta «${e.nome}»` : 'metti un’etichetta che non c’è più'
    }
    if (a.tipo === 'ferma') return 'ferma le regole successive'
    return 'azione sconosciuta'
  }).join(', ')
  return { se: `Se ${se}`, allora }
}
